import Dexie, { Table } from "dexie";
import { Animal, Ubicacion, AnimalHistorico, Unidad, Empresa, Profiles, OperacionCompraVentaAnimales } from "../../../../types/interfaces";
import { Gasto, Ingreso, Stock, StockHistorico, CategoriaCache, Categoria, Concepto } from "../../../../types/administracion";
import { ConceptoOculto, ConceptoConfirmado } from "../../interfaces/IConceptoRepository";
import { SyncAccion } from "../../interfaces/ISyncAccionesRepository";
import { IDatabase } from "../../interfaces/IDatabase";

/**
 * Tablas que usan soft-delete (campo `is_deleted`).
 *
 * En Dexie/IndexedDB almacenamos `is_deleted` como NUMBER (0/1), no como
 * boolean: IndexedDB NO acepta booleanos como clave de índice, así que un
 * índice sobre is_deleted sólo es utilizable vía `where('is_deleted').equals(0)`
 * si el valor es numérico. Los hooks de Dexie (creating/updating/reading)
 * normalizan boolean↔0/1 en el límite del adapter, así que el resto de la app
 * (repositorios, servicios, sync) sigue operando con booleanos. Esto además
 * alinea la representación con SQLite mobile (que usa INTEGER 0/1).
 */
const SOFT_DELETE_TABLES = [
  'animales',
  'animales_historico',
  'gastos',
  'ingresos',
  'notas',
  'nota_anclas',
  'personas_campo',
  'apodos',
  'eventos',
  'recordatorios',
  'lluvias',
  'elementos_mapa',
  'puntos_control',
  'puntos_control_eventos',
  'siembras_lote',
  'sigsa_declaraciones',
  'operacion_compra_venta_animales',
  'stocks_historico',
  'ubicaciones',
  'unidades',
] as const;

/**
 * Dexie DB Definition
 * Base de datos IndexedDB para la aplicación web.
 */
export class DexieDatabase extends Dexie implements IDatabase {
  // Tablas principales
  animales!: Table<Animal, [string, string]>;
  ubicaciones!: Table<Ubicacion, string>;
  unidades!: Table<Unidad, string>;
  empresas!: Table<Empresa, string>;
  animales_historico!: Table<AnimalHistorico, string>;
  profiles!: Table<Profiles, number>;

  // Tablas de Administración
  gastos!: Table<Gasto, string>;
  ingresos!: Table<Ingreso, string>;
  stocks!: Table<Stock, string>;
  stocks_historico!: Table<StockHistorico, string>;
  categorias_cache!: Table<CategoriaCache, string>;
  operacion_compra_venta_animales!: Table<OperacionCompraVentaAnimales, string>;

  // Taxonomía de conceptos (spec 2026-08-07)
  categorias!: Table<Categoria, string>;
  conceptos!: Table<Concepto, string>;
  conceptos_ocultos!: Table<ConceptoOculto, [string, string]>;
  conceptos_confirmados!: Table<ConceptoConfirmado, [string, string]>;

  // Tabla de notas con rollback
  notas!: Table<any, string>;

  // Tablas de herramientas
  eventos!: Table<any, string>;
  recordatorios!: Table<any, string>;
  lluvias!: Table<any, string>;
  elementos_mapa!: Table<any, string>;

  // Puntos de control del mapa (observaciones históricas)
  puntos_control!: Table<any, string>;
  puntos_control_eventos!: Table<any, string>;
  // Lotes de siembra: siembras de cada lote (elementos_mapa tipo SIEMBRA)
  siembras_lote!: Table<any, string>;
  // Notas ancladas (spec 2026-10-16)
  nota_anclas!: Table<any, string>;
  personas_campo!: Table<any, string>;
  // Apodos de sólo lectura (contraparte, 2026-10-18)
  apodos!: Table<any, string>;

  // Tabla de declaraciones SIGSA
  sigsa_declaraciones!: Table<any, string>;

  // Cola de acciones con ejecución server-side (append-only: los estados
  // los setea el handler; sin is_deleted)
  sync_acciones!: Table<SyncAccion, string>;

  private _isReady: boolean = false;

  constructor() {
    super("CattleManagementDB");

    this.version(1).stores({
      animales: "caravana, ubicacion_id, fecha_de_creacion, estado, raza, categoria, edad",
      ubicaciones: "id, nombre, unidad_id",
      animales_historico: "id, caravana, fecha_de_creacion, [caravana+fecha_de_creacion]",
      unidades: "id, nombre",
      empresas: "id, nombre",
      profiles: "empresa_activa_id,user_id,updated_at",
      gastos: "id, fecha_evento, concepto, categoria, ubicacion_id,empresa_id,user_id",
      stocks: "id, nombre, categoria, empresa_id",
      stocks_historico: "id, stock_id, fecha_evento, tipo_operacion,empresa_id,user_id,nombre,categoria",
      categorias_cache: "id, tipo, nombre, uso_count"
    });

    // Add herramientas tables in subsequent versions
    this.version(2).stores({
      eventos: 'id, title, description, date, time, category, completed, user_id, empresa_id, is_public'
    });

    this.version(3).stores({
      notas: 'id, title, content, created_at, updated_at, category, user_id, empresa_id, is_public'
    });

    this.version(4).stores({
      recordatorios: 'id, title, description, date, time, priority, completed, user_id, empresa_id, is_public'
    });

    // Add privacy fields and is_deleted to herramientas tables
    this.version(5).stores({
      eventos: 'id, title, description, date, time, category, completed, user_id, empresa_id, is_public, is_deleted',
      notas: 'id, title, content, created_at, updated_at, category, user_id, empresa_id, is_public, is_deleted',
      recordatorios: 'id, title, description, date, time, priority, completed, user_id, empresa_id, is_public, is_deleted'
    }).upgrade(async (trans) => {
      // Migrate existing eventos
      const oldEventos = await trans.table('eventos').toArray();
      await trans.table('eventos').clear();
      await trans.table('eventos').bulkAdd(oldEventos.map(evento => ({
        ...evento,
        user_id: '',
        empresa_id: '',
        is_public: true
      })));

      // Migrate existing notas
      const oldNotas = await trans.table('notas').toArray();
      await trans.table('notas').clear();
      await trans.table('notas').bulkAdd(oldNotas.map(nota => ({
        ...nota,
        user_id: '',
        empresa_id: '',
        is_public: true
      })));

      // Migrate existing recordatorios
      const oldRecordatorios = await trans.table('recordatorios').toArray();
      await trans.table('recordatorios').clear();
      await trans.table('recordatorios').bulkAdd(oldRecordatorios.map(recordatorio => ({
        ...recordatorio,
        user_id: '',
        empresa_id: '',
        is_public: true
      })));
    });

    // Add rollback fields to notas table
    this.version(6).stores({
      notas: 'id, title, content, created_at, updated_at, category, user_id, empresa_id, is_public, is_deleted, created_by, previous_content, previous_updated_at'
    });

    // Add role field to profiles table
    this.version(7).stores({
      profiles: 'empresa_activa_id,user_id,role'
    });

    // Add pesajes and prenies_historial fields to animales table
    this.version(8).stores({
      animales: 'caravana, ubicacion_id, fecha_de_creacion, estado, raza, categoria, edad, pesajes, prenies_historial'
    });

    // Version 9: Add ingresos table
    this.version(9).stores({
      ingresos: 'id, fecha_evento, concepto, categoria, empresa_id, user_id'
    });

    // Version 10: Remove is_deleted from eventos and recordatorios
    this.version(10).stores({
      eventos: 'id, title, description, date, time, category, completed, user_id, empresa_id, is_public',
      recordatorios: 'id, title, description, date, time, priority, completed, user_id, empresa_id, is_public'
    });

    this.version(11).stores({
      operacion_compra_venta_animales: "uuid, fecha_de_evento, es_venta, [fecha_de_evento+es_venta]"
    });

    // Version 12: Add updated_at and fecha_de_update_backend indices for sync
    this.version(12).stores({
      animales: 'caravana, ubicacion_id, fecha_de_creacion, fecha_de_update_backend, estado, raza, categoria, edad, pesajes, prenies_historial',
      animales_historico: 'id, caravana, fecha_de_creacion, fecha_de_update_backend, [caravana+fecha_de_creacion]',
      operacion_compra_venta_animales: "uuid, fecha_de_evento, updated_at, es_venta, [fecha_de_evento+es_venta]",
      gastos: "id, fecha_evento, updated_at, concepto, categoria, ubicacion_id,empresa_id,user_id",
      ingresos: 'id, fecha_evento, updated_at, concepto, categoria, empresa_id, user_id',
      eventos: 'id, title, description, date, time, category, completed, user_id, empresa_id, is_public, updated_at',
      recordatorios: 'id, title, description, date, time, priority, completed, user_id, empresa_id, is_public, updated_at',
      stocks_historico: 'id, stock_id, fecha_evento, updated_at, tipo_operacion,empresa_id,user_id,nombre,categoria'
    });

    // Version 13: Add updated_at index to recordatorios for sync
    this.version(13).stores({
      recordatorios: 'id, title, description, date, time, priority, completed, user_id, empresa_id, is_public, updated_at'
    });

    // Add role field to profiles table
    this.version(14).stores({
      profiles: 'empresa_activa_id,user_id,role, updated_at',
      empresas: "id, nombre, updated_at",
      unidades: "id, nombre, updated_at",
      ubicaciones: "id, nombre, unidad_id, updated_at"
    });

    // Version 15: Migrate raza values to SIGSA codes
    // Converts old enum values (ANGUS, HEREFORD, CARETA) to SIGSA codes (AA, H, OR)
    this.version(15).stores({
      // No schema changes, just data migration
    }).upgrade(async (trans) => {
      // Mapeo de valores viejos a códigos SIGSA
      const razaMapping: Record<string, string> = {
        'ANGUS': 'AA',
        'HEREFORD': 'H',
        'CARETA': 'OR'
      };

      // Migrate animales table
      const animales = await trans.table('animales').toArray();
      const animalesUpdates: Array<{ key: string; changes: { raza?: string } }> = [];

      for (const animal of animales) {
        if (animal.raza && razaMapping[animal.raza.toUpperCase()]) {
          animalesUpdates.push({
            key: animal.caravana,
            changes: { raza: razaMapping[animal.raza.toUpperCase()] }
          });
        }
      }

      // Batch update animales
      for (const update of animalesUpdates) {
        await trans.table('animales').update(update.key, update.changes);
      }

      // Migrate animales_historico table
      const historico = await trans.table('animales_historico').toArray();
      const historicoUpdates: Array<{ key: string; changes: { raza?: string } }> = [];

      for (const record of historico) {
        if (record.raza && razaMapping[record.raza.toUpperCase()]) {
          historicoUpdates.push({
            key: record.id!,
            changes: { raza: razaMapping[record.raza.toUpperCase()] }
          });
        }
      }

      // Batch update animales_historico
      for (const update of historicoUpdates) {
        await trans.table('animales_historico').update(update.key, update.changes);
      }
    });

    // Version 16: Add renspa field to animales table
    this.version(16).stores({
      animales: 'caravana, ubicacion_id, fecha_de_creacion, fecha_de_update_backend, estado, raza, categoria, edad, pesajes, prenies_historial, renspa',
      animales_historico: 'id, caravana, fecha_de_creacion, fecha_de_update_backend, [caravana+fecha_de_creacion]'
    });

    // Version 17: Fix stocks table schema and repair missing nombre/categoria fields
    this.version(17).stores({
      // Explicitly redefine stocks schema to ensure all fields are persisted
      stocks: 'id, nombre, categoria, empresa_id, updated_at, fecha_evento, unidad_medida, cantidad, tipo_operacion, fecha_creacion',
      stocks_historico: 'id, stock_id, fecha_evento, updated_at, tipo_operacion,empresa_id,user_id,nombre,categoria'
    }).upgrade(async (trans) => {
      // Repair stocks that are missing nombre or categoria fields
      const stocks = await trans.table('stocks').toArray();
      const stocks_historico = await trans.table('stocks_historico').toArray();

      // Build a map of stock_id to latest historico record for each stock
      const historicoMap = new Map<string, any>();
      for (const historico of stocks_historico) {
        const existing = historicoMap.get(historico.stock_id);
        if (!existing || new Date(historico.updated_at || '') > new Date(existing.updated_at || '')) {
          historicoMap.set(historico.stock_id, historico);
        }
      }

      // Update stocks that are missing nombre or categoria
      for (const stock of stocks) {
        const updates: Partial<any> = {};
        let needsUpdate = false;

        if (!stock.nombre) {
          const historico = historicoMap.get(stock.id);
          if (historico?.nombre) {
            updates.nombre = historico.nombre;
            needsUpdate = true;
          }
        }

        if (!stock.categoria) {
          const historico = historicoMap.get(stock.id);
          if (historico?.categoria) {
            updates.categoria = historico.categoria;
            needsUpdate = true;
          }
        }

        if (needsUpdate) {
          await trans.table('stocks').update(stock.id, updates);
          console.log('Repaired stock:', stock.id, updates);
        }
      }
    });

    // Version 18: Add es_comprado field to animales table
    this.version(18).stores({
      animales: 'caravana, ubicacion_id, fecha_de_creacion, fecha_de_update_backend, estado, raza, categoria, edad, pesajes, prenies_historial, renspa, es_comprado',
      animales_historico: 'id, caravana, fecha_de_creacion, fecha_de_update_backend, [caravana+fecha_de_creacion]'
    });

    // Version 18: Add updated_at index to animales table for intelligent sync
    this.version(19).stores({
      animales: 'caravana, ubicacion_id, fecha_de_creacion, fecha_de_update_backend, updated_at, estado, raza, categoria, edad, pesajes, prenies_historial, renspa, es_comprado'
    });

    // Version 20: Add ubicaciones_historial field to animales table
    this.version(20).stores({
      animales: 'caravana, ubicacion_id, fecha_de_creacion, fecha_de_update_backend, updated_at, estado, raza, categoria, edad, pesajes, prenies_historial, renspa, es_comprado, ubicaciones_historial'
    });

    // Version 21: PK compuesta (empresa_id, caravana) en animales.
    //
    // IndexedDB/Dexie NO permite cambiar el keyPath (PK) de un store existente
    // → para pasar de `caravana` a `[empresa_id+caravana]` hay que ELIMINAR el
    // store viejo y recrearlo con la nueva PK. Como `animales` es un caché
    // derivado (se reconstruye desde Supabase + animales_historico), vaciarlo
    // es seguro: el próximo sync detecta la tabla vacía y hace pull completo.
    // Por eso se hace en dos versiones:
    //   v21 → elimina el store viejo (PK caravana)
    //   v22 → recrea el store con PK compuesta
    this.version(21).stores({
      animales: null
    });

    this.version(22).stores({
      animales: '[empresa_id+caravana], empresa_id, ubicacion_id, fecha_de_creacion, fecha_de_update_backend, updated_at, estado, raza, categoria, edad, pesajes, prenies_historial, renspa, es_comprado, ubicaciones_historial'
    });

    // Version 23: índice `is_deleted` en las tablas soft-delete.
    //
    // El pull (syncPull) ahora CONSERVA los registros is_deleted=true en el
    // caché local (los hard-deleteaba). Para que el filtrado por is_deleted de
    // las lecturas (getAll/queries) sea eficiente y no un scan en memoria,
    // indexamos la columna. notas ya la tenía (v5/v6); el resto se agrega acá.
    // Dexie reindexa automáticamente al abrir; is_deleted es dato preexistente.
    this.version(23).stores({
      animales: '[empresa_id+caravana], empresa_id, ubicacion_id, fecha_de_creacion, fecha_de_update_backend, updated_at, estado, raza, categoria, edad, pesajes, prenies_historial, renspa, es_comprado, ubicaciones_historial, is_deleted',
      gastos: 'id, fecha_evento, updated_at, concepto, categoria, ubicacion_id, empresa_id, user_id, is_deleted',
      ingresos: 'id, fecha_evento, updated_at, concepto, categoria, empresa_id, user_id, is_deleted',
      eventos: 'id, title, description, date, time, category, completed, user_id, empresa_id, is_public, updated_at, is_deleted',
      recordatorios: 'id, title, description, date, time, priority, completed, user_id, empresa_id, is_public, updated_at, is_deleted',
      operacion_compra_venta_animales: 'uuid, fecha_de_evento, updated_at, es_venta, [fecha_de_evento+es_venta], is_deleted',
      stocks_historico: 'id, stock_id, fecha_evento, updated_at, tipo_operacion, empresa_id, user_id, nombre, categoria, is_deleted'
    });

    // Version 24: índice `is_deleted` en ubicaciones y unidades.
    //
    // Estas tablas pasaron de hard-delete a soft-delete (UPDATE is_deleted=true)
    // para que el catch-up incremental propague los borrados (online u offline).
    // Igual que en v23, indexamos is_deleted para que el filtrado de las
    // lecturas use el índice y no scanee en memoria.
    this.version(24).stores({
      ubicaciones: 'id, nombre, unidad_id, updated_at, is_deleted',
      unidades: 'id, nombre, updated_at, is_deleted'
    });

    // Version 25: normalizar `is_deleted` a NUMBER (0/1) en todas las tablas
    // soft-delete. IndexedDB no acepta booleanos como clave de índice, así que
    // para que el índice sobre is_deleted sea utilizable vía where().equals(0)
    // el valor almacenado debe ser numérico. Los hooks (creados abajo) hacen la
    // conversión en new-writes; acá migramos los registros existentes.
    this.version(25).stores({}).upgrade(async (trans) => {
      for (const table of SOFT_DELETE_TABLES) {
        try {
          const rows = await trans.table(table).toArray();
          let changed = 0;
          for (const row of rows) {
            if (typeof row.is_deleted === 'boolean') {
              await trans.table(table).put({ ...row, is_deleted: row.is_deleted ? 1 : 0 });
              changed++;
            }
          }
          if (changed > 0) {
            console.log(`[Dexie v25] ${table}: ${changed} filas is_deleted convertidas a 0/1`);
          }
        } catch {
          // tabla sin is_deleted o no presente → skip
        }
      }
    });

    // Version 26: Taxonomía de conceptos (spec 2026-08-07): categorías, conceptos
    // y las tablas de relación por empresa. `conceptos_ocultos`/`conceptos_confirmados`
    // tienen PK compuesta (empresa_id, concepto_id) igual que en Supabase (no hay
    // columna id): una fila por pareja empresa+concepto. Tablas nuevas: Dexie las
    // crea vacías y se pueblan por sync.
    this.version(26).stores({
      categorias: 'id, nombre, empresa_id, tipo, activo',
      conceptos: 'id, nombre, categoria_id, empresa_id, activo',
      conceptos_ocultos: '[empresa_id+concepto_id], empresa_id, concepto_id',
      conceptos_confirmados: '[empresa_id+concepto_id], empresa_id, concepto_id',
    });

    // Version 27: fuera el índice `tipo_operacion` de stocks (spec §12.4).
    //
    // El flag ENGORDE/GASTO_GENERAL murió: la clase de un gasto vive en
    // concepto→categoría→tipo (read-time, clasificacionEngorde) y ningún
    // writer lo escribe desde P3b T7 (la columna tipo_operacion de gastos se
    // dropea en Supabase; stocks es tabla local-only y su campo quedó huérfano).
    // Dexie borra el índice al omitirlo del schema de la versión nueva; los
    // registros viejos conservan el dato (inerte, nadie lo lee).
    this.version(27).stores({
      stocks: 'id, nombre, categoria, empresa_id, updated_at, fecha_evento, unidad_medida, cantidad, fecha_creacion'
    });

    // Version 28: Lluvias (KAN-87) — registro de precipitaciones por empresa.
    //
    // Tabla nueva: Dexie la crea vacía y se puebla por sync (pull inicial /
    // realtime). Sin upgrade. Índice is_deleted igual que el resto de las
    // tablas soft-delete (v23) para que las lecturas lo filtren por índice.
    this.version(28).stores({
      lluvias: 'id, fecha, empresa_id, ubicacion_id, user_id, updated_at, is_deleted'
    });

    // Version 29: Elementos de Mapa (Mapa del Campo) — elementos georreferenciados
    // dibujados por el usuario (cuadros, corrales, aguadas, paneles, puestos...).
    //
    // Tabla nueva: Dexie la crea vacía y se puebla por sync (pull inicial /
    // realtime). Sin upgrade. La geometría GeoJSON vive como objeto inline
    // (Dexie indexa por propiedades, no hace falta indexar coordinates).
    this.version(29).stores({
      elementos_mapa: 'id, tipo, empresa_id, ubicacion_id, user_id, updated_at, is_deleted'
    });

    // Version 30: Fotos de elementos del mapa PENDIENTES DE SUBIR (cola
    // offline): tabla LOCAL-ONLY con blobs — NO entra al sync (pending_changes
    // lleva filas, no archivos). La saca procesarCola() al subir a Storage.
    this.version(30).stores({
      media_pendiente: 'id, elemento_id, creado_en'
    });

    // Version 31: Declaraciones SIGSA — registro de qué caravanas fueron
    // declaradas en el portal SIGSA (SENASA).
    //
    // Tabla nueva: Dexie la crea vacía y se puebla por sync (pull inicial /
    // realtime). Sin upgrade. `caravanas` es un array de strings inline
    // (Dexie guarda objetos JS nativos, no hace falta indexarlo).
    this.version(31).stores({
      sigsa_declaraciones: 'id, empresa_id, session_code, fecha_declaracion, updated_at, is_deleted'
    });

    // Version 32: arrays/objetos de `animales` en formato nativo (no JSON string).
    //
    // Antes el repositorio stringificaba pesajes/prenies_historial/etc. antes
    // del put (herencia de SQLite), mientras que el catch-up realtime guardaba
    // la fila de Supabase con arrays nativos → dos formatos conviviendo y nadie
    // que deserialice al leer. IndexedDB soporta structured clone, así que el
    // formato canónico pasa a ser array/objeto nativo (mismo criterio que la
    // v31 para `caravanas`). Los índices sobre esos campos se dropean: nadie
    // querya por ellos y como arrays de objetos ni siquiera serían claves
    // válidas de índice. El upgrade normaliza las filas legacy (strings →
    // arrays) para que el caché quede consistente en un solo formato.
    this.version(32).stores({
      animales: '[empresa_id+caravana], empresa_id, ubicacion_id, fecha_de_creacion, fecha_de_update_backend, updated_at, estado, raza, categoria, edad, renspa, es_comprado, is_deleted'
    }).upgrade(async (trans) => {
      const table = trans.table('animales');
      const rows = await table.toArray();
      let changed = 0;
      for (const row of rows) {
        const fixed: Record<string, unknown> = {};
        const jsonFields = [
          'pesajes', 'prenies_historial', 'ubicaciones_historial',
          'caravanas_viejas', 'info_compra', 'info_venta',
        ];
        for (const field of jsonFields) {
          const value = (row as Record<string, unknown>)[field];
          if (typeof value === 'string') {
            try {
              fixed[field] = JSON.parse(value);
            } catch {
              // string no-JSON (¿dato corrupto?) → dejarlo como está
            }
          }
        }
        if (Object.keys(fixed).length > 0) {
          await table.put({ ...row, ...fixed });
          changed++;
        }
      }
      if (changed > 0) {
        console.log(`[Dexie v32] animales: ${changed} filas con arrays JSON-string normalizadas a nativos`);
      }
    });

    // Version 33: Puntos de control del mapa — puntos fijos con observaciones
    // repetidas en el tiempo (condición de pastura, plantas, ambiente,
    // densidad, fotos fechadas).
    //
    // Tablas nuevas: Dexie las crea vacías y se pueblan por sync (pull
    // inicial / realtime). Sin upgrade. `fotos` de los eventos vive como
    // STRING JSON (el repo stringifica: paridad con SQLite mobile); los
    // BLOBS de fotos pendientes de subir van a la cola local-only de abajo.
    this.version(33).stores({
      puntos_control: 'id, empresa_id, creado_por, updated_at, is_deleted',
      puntos_control_eventos: 'id, punto_id, empresa_id, creado_por, fecha_observacion, updated_at, is_deleted',
      // Cola offline de FOTOS (local-only, blobs): NO entra al sync
      // (pending_changes lleva filas, no archivos). La saca
      // procesarColaFotos() al subir al bucket puntos-control-media.
      puntos_control_media_pendiente: 'id, evento_id, creado_en'
    });

    // Version 34: sync_acciones — cola de acciones con ejecución server-side
    // (spec alta-masiva-sin-caravana §3; migración Supabase 20261001130000).
    // Tabla nueva: Dexie la crea vacía y se puebla por sync (FULL SNAPSHOT en
    // SyncManager, sin cursor incremental).
    //
    // FIX: sin esta declaración, DexieAdapter accedía a `db.sync_acciones`
    // (undefined) → SyncAccionesRepository.getAll/bulkUpsert tiraban
    // "Cannot read properties of undefined" y el error cortaba el pull
    // inicial completo ("Última sync: Nunca") en navegadores sin caché.
    // Índices espejo de Supabase: listado por empresa (fecha desc) y el filtro
    // de pendientes/fallidas por estado. Append-only: estado/resultado los
    // setea el handler server-side; no está en SOFT_DELETE_TABLES
    // (isDeletedLogic: false en SyncManager).
    this.version(34).stores({
      sync_acciones: 'id, empresa_id, estado, created_at, updated_at'
    });

    // Version 35: mapa multi-campo — la unidad/campo baja al mapa y a la
    // lluvia (migración Supabase 20261015150000). elementos_mapa.unidad_id
    // (CUADRO/CORRAL la toman de la ubicación vinculada, el resto hereda por
    // contención) y lluvias.unidad_id (pluviómetro por campo). Sin upgrade:
    // columnas nullable, se pueblan por sync.
    this.version(35).stores({
      elementos_mapa: 'id, tipo, empresa_id, unidad_id, ubicacion_id, user_id, updated_at, is_deleted',
      lluvias: 'id, fecha, empresa_id, unidad_id, ubicacion_id, user_id, updated_at, is_deleted'
    });

    // Version 36: notas ancladas (spec aiChatBot 2026-10-16, migración Supabase
    // 20261016100000). Tablas nuevas nota_anclas (anclas de notas; `caravanas`
    // multiEntry → "notas de la caravana X" indexado) y personas_campo (gente
    // SIN cuenta). Los `apodos` NO se sincronizan: son memoria del asistente. notas suma
    // tipo/fecha_evento/realizado_por_user_id/realizado_por_persona_id
    // indexados. Sin upgrade: columnas/tablas nuevas se pueblan por sync.
    this.version(36).stores({
      notas: 'id, title, content, created_at, updated_at, category, user_id, empresa_id, is_public, is_deleted, created_by, previous_content, previous_updated_at, tipo, fecha_evento, realizado_por_user_id, realizado_por_persona_id',
      nota_anclas: 'id, empresa_id, nota_id, tipo_ancla, ancla_id, *caravanas, updated_at, is_deleted',
      personas_campo: 'id, empresa_id, updated_at, is_deleted'
    });

    // Version 37: contraparte en la plata (migración Supabase 20261018100000). Los
    // `apodos` bajan de SÓLO LECTURA para el autocompletar de "a quién / de quién"
    // ("Juancho" → Juan Pérez); se siguen creando sólo desde el asistente. Las columnas
    // contraparte_* de gastos/ingresos/stocks/operaciones no necesitan índice.
    this.version(37).stores({
      apodos: 'id, empresa_id, tipo_entidad, entidad_id, updated_at, is_deleted'
    });

    // Version 38: lotes de siembra (migración Supabase 20261101120000). Tabla nueva
    // siembras_lote: una fila por siembra dentro de un lote (elementos_mapa tipo SIEMBRA);
    // Dexie la crea vacía y se puebla por sync (pull inicial / realtime). Índices: el
    // historial de un lote (elemento_id), el pull/cursor por empresa y el filtro de borradas.
    // `resumen` (JSONB) vive como STRING JSON en el caché: lo (de)serializa el repositorio.
    // elementos_mapa.lote_estado / riego son columnas nuevas SIN índice: no cambian el store
    // (lote_estado ausente en el caché viejo = 'confirmado'). Sin upgrade.
    this.version(38).stores({
      siembras_lote: 'id, elemento_id, empresa_id, updated_at, is_deleted'
    });

    // Hooks de normalización is_deleted: la app usa booleanos, Dexie guarda 0/1.
    this.registerIsDeletedHooks();

    this._isReady = true;
  }

  /**
   * Registra hooks de Dexie para que `is_deleted` se guarde como NUMBER (0/1)
   * y se devuelva como BOOLEAN al leer. Así el resto de la app opera con
   * booleanos (sin tocar repositorios/servicios/sync) y el índice queda
   * utilizable (IDB no admite booleanos como clave).
   */
  private registerIsDeletedHooks(): void {
    for (const table of SOFT_DELETE_TABLES) {
      const t = (this as any)[table];
      if (!t || typeof t.hook !== 'function') continue;

      // creating: nuevo registro → boolean a 0/1 antes de persistir.
      t.hook('creating', function (_primKey: any, obj: any) {
        if (obj && typeof obj.is_deleted === 'boolean') {
          obj.is_deleted = obj.is_deleted ? 1 : 0;
        }
      });

      // updating: modificación → si incluye is_deleted boolean, convertir a 0/1.
      // Retornar un objeto reemplaza las modifications; undefined = no-op.
      t.hook('updating', function (mods: any) {
        if (mods && typeof mods.is_deleted === 'boolean') {
          return { ...mods, is_deleted: mods.is_deleted ? 1 : 0 };
        }
        return undefined;
      });

      // reading: al leer, 0/1 → boolean (la app sigue viendo true/false).
      t.hook('reading', function (obj: any) {
        if (obj && typeof obj.is_deleted === 'number') {
          obj.is_deleted = obj.is_deleted === 1;
        }
        return obj;
      });
    }
  }

  /**
   * Inicializa la base de datos (implementación de IDatabase)
   */
  async initialize(): Promise<void> {
    // Dexie se inicializa automáticamente en el constructor
    // Este método existe para consistencia con la interfaz
    await this.open();
  }

  /**
   * Verifica si la base de datos está lista
   */
  isReady(): boolean {
    return this._isReady && this.isOpen();
  }

  /**
   * Limpia todos los datos de las tablas principales
   */
  async clearAllData(): Promise<void> {
    try {
      await Promise.all([
        this.animales.clear(),
        this.ubicaciones.clear(),
        this.unidades.clear(),
        this.empresas.clear(),
        this.animales_historico.clear(),
        this.profiles.clear(),
        this.operacion_compra_venta_animales.clear()
      ]);
    } catch (error) {
      console.error('Error clearing all data:', error);
    }
  }

  /**
   * Cierra la conexión a la base de datos
   *
   * NOTA: debe llamar a `super.close()` (Dexie) — NO `this.close()`,
   * que recursiona infinitamente y agota el stack (RangeError),
   * dejando la conexión en estado "closing" y rompiendo todas las
   * transacciones posteriores.
   */
  async close(): Promise<void> {
    super.close();
    this._isReady = false;
  }
}

// Instancia singleton para uso en web
let _instance: DexieDatabase | null = null;

/**
 * Obtiene la instancia singleton de la base de datos Dexie.
 *
 * Si la instancia existe pero está cerrada (p.ej. tras un `close()` por
 * logout, HMR o un evento `versionchange` de otra pestaña), se reabre
 * automáticamente para que el singleton siga siendo usable y no quede en
 * estado "closing" rompiendo todas las transacciones posteriores.
 */
export function getDexieDatabase(): DexieDatabase {
  if (!_instance) {
    _instance = new DexieDatabase();
  }
  if (!_instance.isOpen()) {
    // Reabre si fue cerrada; `open()` es seguro invocarlo siempre (idempotente).
    _instance.open().catch((err) => console.warn('[DexieDatabase] re-open falló:', err));
  }
  return _instance;
}
